import { Injectable, inject, signal, untracked } from '@angular/core';

import {
  DeployContentKind,
  DeployPlanConfigItem,
  DeployPlanMessagesResponse
} from '@core/models/deploy-plan-config.model';
import { DeployPlanConfigService } from '@core/services/deploy-plan-config/deploy-plan-config.service';

export interface DeployChannelState {
  loading: boolean;
  error: boolean;
  /** A refetch running behind a response that is still on screen. See `refresh`. */
  refreshing: boolean;
  response: DeployPlanMessagesResponse | null;
}

const IDLE: DeployChannelState = {
  loading: false,
  error: false,
  refreshing: false,
  response: null
};

/**
 * Provided by the deploy page rather than in root: the cache is scoped to one visit of one
 * plan, and a `/messages` response is only valid for the plan it was fetched against.
 *
 * Held here rather than in the channel panel because the panel is destroyed whenever the
 * operator selects another channel, which would drop both the cache and the selection.
 */
@Injectable()
export class DeployMessagesStore {
  private readonly service = inject(DeployPlanConfigService);

  private readonly states = signal<ReadonlyMap<string, DeployChannelState>>(new Map());
  private readonly selections = signal<ReadonlyMap<string, ReadonlySet<string>>>(new Map());

  /**
   * Uids handed to an accepted deploy that has not reported back yet, per configuration. Held
   * here rather than in the panel because the panel is destroyed on every channel switch,
   * which would lose the in-flight state of a deploy that is still running.
   *
   * A `view`-kind channel has no uids, so an empty set still means "this one is deploying" —
   * read it through `isDeploying`, never through `deploying(id).size`.
   */
  private readonly deployingUids = signal<ReadonlyMap<string, ReadonlySet<string>>>(
    new Map()
  );

  /** Generation per configuration, bumped by `invalidate` so a late response is dropped. */
  private readonly generations = new Map<string, number>();

  state(configId: string): DeployChannelState {
    return this.states().get(configId) ?? IDLE;
  }

  selection(configId: string): ReadonlySet<string> {
    return this.selections().get(configId) ?? new Set<string>();
  }

  deploying(configId: string): ReadonlySet<string> {
    return this.deployingUids().get(configId) ?? new Set<string>();
  }

  isDeploying(configId: string): boolean {
    return this.deployingUids().has(configId);
  }

  /** Called on the 202, so the rows the operator picked change before the channel reports. */
  markDeploying(configId: string, uids: Iterable<string>): void {
    this.deployingUids.update((deploying) =>
      new Map(deploying).set(configId, new Set(uids))
    );
  }

  clearDeploying(configId: string): void {
    this.deployingUids.update((deploying) => {
      if (!deploying.has(configId)) return deploying;
      const next = new Map(deploying);
      next.delete(configId);
      return next;
    });
  }

  /** Fetches once per configuration; a cached or in-flight one is left alone. */
  load(config: DeployPlanConfigItem, planId: string): void {
    const current = this.current(config.id);
    if (current.loading || current.response !== null) return;

    const generation = (this.generations.get(config.id) ?? 0) + 1;
    this.generations.set(config.id, generation);
    this.patchState(config.id, {
      loading: true,
      error: false,
      refreshing: false,
      response: null
    });

    this.service.messages$(config.id, planId).subscribe({
      next: (response) => {
        if (this.generations.get(config.id) !== generation) return;
        this.patchState(config.id, {
          loading: false,
          error: false,
          refreshing: false,
          response
        });
      },
      error: () => {
        if (this.generations.get(config.id) !== generation) return;
        this.patchState(config.id, {
          loading: false,
          error: true,
          refreshing: false,
          response: null
        });
      }
    });
  }

  /**
   * Refetches behind the response already on screen, so a write that changes one row does
   * not read as the whole channel reloading. `invalidate` followed by `load` cannot do this:
   * `invalidate` clears the response, which unmounts the panel's content, and `load` then
   * renders its spinner in the gap.
   *
   * A failed refresh keeps the stale response instead of raising the error banner. The write
   * that triggered it has already succeeded, so content the operator can still read beats an
   * empty pane; the next visit to the channel fetches again.
   */
  refresh(config: DeployPlanConfigItem, planId: string): void {
    const current = this.current(config.id);
    if (current.response === null) {
      this.load(config, planId);
      return;
    }

    const generation = (this.generations.get(config.id) ?? 0) + 1;
    this.generations.set(config.id, generation);
    this.patchState(config.id, { ...current, refreshing: true, error: false });

    this.service.messages$(config.id, planId).subscribe({
      next: (response) => {
        if (this.generations.get(config.id) !== generation) return;
        // Deliberately no `seedSelection`: a refresh only happens behind a response the
        // operator has already been ticking, and reseeding would discard those ticks.
        this.patchState(config.id, {
          loading: false,
          error: false,
          refreshing: false,
          response
        });
      },
      error: () => {
        if (this.generations.get(config.id) !== generation) return;
        this.patchState(config.id, { ...this.current(config.id), refreshing: false });
      }
    });
  }

  /**
   * Refetch a channel the operator is looking at, and only that. `refresh` falls back to
   * `load` when nothing is cached, which on the post-deploy path would fire a `/messages`
   * call — a full pipeline run plus a gRPC plan fetch — for a channel nobody has opened.
   */
  refreshIfLoaded(config: DeployPlanConfigItem, planId: string): void {
    if (this.current(config.id).response === null) return;
    this.refresh(config, planId);
  }

  /**
   * Settles the table after a deploy at a fraction of the cost of `refreshIfLoaded`: a deploy
   * changes the attempts and nothing else, and `/sends` returns those alone where `/messages`
   * re-runs every preprocessor and re-renders every body to hand back identical content.
   *
   * A `view`-kind channel keeps the full refresh — it has no per-message history to merge, and
   * its content is one small body.
   */
  refreshSendsIfLoaded(config: DeployPlanConfigItem, planId: string): void {
    const current = this.current(config.id);
    if (current.response === null) return;
    if (current.response.contentKind !== DeployContentKind.MESSAGES) {
      this.refresh(config, planId);
      return;
    }

    const generation = (this.generations.get(config.id) ?? 0) + 1;
    this.generations.set(config.id, generation);
    this.patchState(config.id, { ...current, refreshing: true, error: false });

    this.service.sends$(config.id, planId).subscribe({
      next: ({ sends }) => {
        if (this.generations.get(config.id) !== generation) return;
        // Re-read rather than merging into the response captured above: the generation guard
        // only rejects responses that lost a race, and the state may have moved on since.
        const latest = this.current(config.id);
        if (latest.response?.contentKind !== DeployContentKind.MESSAGES) {
          this.patchState(config.id, { ...latest, refreshing: false });
          return;
        }

        this.patchState(config.id, {
          ...latest,
          refreshing: false,
          response: {
            ...latest.response,
            content: {
              ...latest.response.content,
              // `[]` for an absent uid, not the cached attempts: the response is the whole
              // history for this (plan, configuration), read from the same source `/messages`
              // embeds, so an omitted uid means "never attempted".
              messages: latest.response.content.messages.map((message) => ({
                ...message,
                sends: sends[message.uid] ?? []
              }))
            }
          }
        });

        // A message that is out is no longer one the operator means to send. Dropping it
        // here rather than on the terminal event is what makes the tick survive exactly as
        // long as the fact does: a failed attempt leaves the row ticked and ready to retry,
        // where clearing the whole selection would make them find it again.
        this.patchSelection(
          config.id,
          new Set(
            [...untracked(() => this.selection(config.id))].filter(
              (uid) => !(sends[uid] ?? []).some((attempt) => attempt.success)
            )
          )
        );
      },
      error: () => {
        if (this.generations.get(config.id) !== generation) return;
        this.patchState(config.id, { ...this.current(config.id), refreshing: false });
      }
    });
  }

  /** Drops the cached response so the next `load` refetches; the selection is kept. */
  invalidate(configId: string): void {
    this.generations.set(configId, (this.generations.get(configId) ?? 0) + 1);
    this.patchState(configId, IDLE);
  }

  toggle(configId: string, uid: string): void {
    const next = new Set(this.selection(configId));
    if (next.has(uid)) next.delete(uid);
    else next.add(uid);
    this.patchSelection(configId, next);
  }

  setSelection(configId: string, uids: Iterable<string>): void {
    this.patchSelection(configId, new Set(uids));
  }

  /**
   * The current state, read outside any reactive context.
   *
   * Every method above is a command, and several are called from an `effect` — the panel's
   * load, the page's post-deploy settle. Reading the state through the tracked getter there
   * makes the effect depend on the state the command is about to write, and `load`'s error
   * path then refetches without end: it leaves `response` null, so the guard that stops the
   * success path never holds.
   */
  private current(configId: string): DeployChannelState {
    return untracked(() => this.state(configId));
  }

  /** `update`, not `set(new Map(this.states()))`: reading the signal here would make every
   * caller's reactive context depend on it, which is the same trap as `current` avoids. */
  private patchState(configId: string, state: DeployChannelState): void {
    this.states.update((states) => new Map(states).set(configId, state));
  }

  private patchSelection(configId: string, uids: ReadonlySet<string>): void {
    this.selections.update((selections) => new Map(selections).set(configId, uids));
  }
}
